import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArtifactRenderer } from '../artifacts/ArtifactRenderer';
import { CitationList } from './CitationList';
import { ConfidenceBadge } from './ConfidenceBadge';
import { InteractiveOptions } from './InteractiveOptions';
import { normalizeMarkdownTables } from '@/utils/markdown';
import { useChatStore } from '@/stores/chatStore';
import { useWriteEnableStore } from '@/stores/writeEnableStore';
import { submitAppName, finishGuidedSetup, retryCredentialCapture } from '@/stores/guidedSetup';
import { takeMeThere } from '@/stores/teleport';
import { teleportTarget } from '@/lib/teleport';
import { documentCardTarget, planCardAction, watchBrowserCard } from '@/lib/plans';
import { usePlansStore } from '@/stores/plansStore';
import { useBrowserStore } from '@/stores/browserStore';
import { useSetupStore, type ZoomAccessLevel } from '@/stores/setupStore';
import { ZOOM_S2S_CREATE_URL, zoomAckText, zoomChoiceLabel } from '@/lib/zoomSetup';
import { cn } from '@/lib/utils';
import type { InteractiveMode, Message as MessageType } from '@/types';

interface MessageProps {
  message: MessageType;
  /** True when the previous message has the same role — the avatar is replaced
   *  by a spacer and vertical padding tightens so same-speaker runs group. */
  grouped?: boolean;
}

export function Message({ message, grouped }: MessageProps) {
  const navigate = useNavigate();
  const { sendMessage, isStreaming, addAssistantMessage, addUserMessage, clearMessageInteractive, setMessageInteractive } = useChatStore();
  const replaceCard = useCallback((next: InteractiveMode) => setMessageInteractive(message.id, next), [setMessageInteractive, message.id]);
  const setAgentControl = useBrowserStore((s) => s.setAgentControl);
  const setPendingNavigateUrl = useBrowserStore((s) => s.setPendingNavigateUrl);
  const setAccessLevel = useSetupStore((s) => s.setAccessLevel);
  const awaitingAppName = useSetupStore((s) => s.awaitingAppName);
  const setAwaitingAppName = useSetupStore((s) => s.setAwaitingAppName);
  const isUser = message.role === 'user';

  const handleOptionSelect = (value: string) => {
    // "Take me there" under a where-answer: the click is the confirmation. ZAC walks the browser panel to the
    // setting (stores/teleport.ts); nothing goes to the chat. Ahead of the app-name step, which would take the value.
    const teleportEntry = teleportTarget(value);
    if (teleportEntry !== null) {
      clearMessageInteractive(message.id);
      const option = message.interactive?.options?.find((o) => o.value === value);
      void takeMeThere({ entryId: teleportEntry, label: option?.description ?? option?.label ?? 'the setting' });
      return;
    }
    // A request's card: Open goes to its page in the Changes tab; nothing goes to the brain.
    const requestId = documentCardTarget(value);
    if (requestId !== null) {
      navigate(`/changes/documents/${requestId}`);
      return;
    }
    // A drafted plan's card (D11): Open plan goes to the Changes tab; Approve & run approves and runs it
    // through the plans service (asking "Watch the browser?" first when it has browser steps and the panel is
    // hidden, D19). Nothing goes to the brain. Ahead of the app-name step, which would take the value.
    const planAction = planCardAction(value);
    if (planAction !== null) {
      clearMessageInteractive(message.id);
      if (planAction.action === 'open') {
        navigate(`/changes/${planAction.planId}`);
        return;
      }
      const hasBrowserSteps = message.interactive?.plan?.hasBrowserSteps ?? false;
      if (planAction.watch === undefined && hasBrowserSteps && !useBrowserStore.getState().panelOpen) {
        void addAssistantMessage('Before I run it:', undefined, watchBrowserCard(planAction.planId), { persist: false });
        return;
      }
      void addUserMessage('Approve and run the plan', { once: true });
      void usePlansStore.getState().approveAndRunFromChat(planAction.planId, planAction.watch ?? false, (line) => void addAssistantMessage(line, undefined, undefined, { once: true, persist: false }));
      return;
    }
    // Guided Zoom setup: the success-card CTA — jump to the Account page.
    if (value === 'zoom-setup:goto-account') {
      clearMessageInteractive(message.id);
      navigate('/account');
      return;
    }
    // Guided Zoom setup: success-card primary CTA — generate the account
    // report with the freshly connected S2S app.
    if (value === 'zoom-setup:generate-report') {
      clearMessageInteractive(message.id);
      navigate('/report');
      return;
    }
    // Guided Zoom setup: user confirmed the manual add-scopes step (or wants a
    // retry) — run the finale chain: activate → identity → verify.
    if (value === 'zoom-setup:scopes-added') {
      clearMessageInteractive(message.id);
      void finishGuidedSetup();
      return;
    }
    // Guided Zoom setup: re-run the Step-1 credential read (app exists, the
    // page read failed/raced) — success continues into info + scopes.
    if (value === 'zoom-setup:retry-capture') {
      clearMessageInteractive(message.id);
      void retryCredentialCapture();
      return;
    }
    // Guided Zoom setup: the app-name step. While ZAC is awaiting the app name,
    // the submitted input value IS the name — create the S2S app, don't send to
    // the brain.
    if (awaitingAppName && !value.startsWith('zoom-setup:')) {
      // Clear the widget UI here (we have the message id), then delegate the
      // actual create flow to the shared store action — the SAME path the main
      // composer now takes via sendMessage's guard, so both surfaces behave
      // identically and the logic lives in one place.
      clearMessageInteractive(message.id);
      setAwaitingAppName(false);
      if (!value.trim()) return;
      void submitAppName(value);
      return;
    }
    // Guided Zoom setup choices are handled client-side, not sent to the brain.
    // The access level is the entry point for the guided provisioning flow; the
    // sidecar sign-in + app provisioning are the next steps from here.
    if (value.startsWith('zoom-setup:')) {
      const purpose = value.slice('zoom-setup:'.length) as ZoomAccessLevel; // 'reader' | 'writer'
      // Choice made — drop the buttons so they don't linger.
      clearMessageInteractive(message.id);
      // Persist the choice so the setup chat rehydrates to this step on reload.
      setAccessLevel(purpose);
      // Record the choice as a user message so the transcript shows it.
      addUserMessage(zoomChoiceLabel(purpose), { once: true });
      // ZAC drives straight to the Server-to-Server app builder (an auth-gated
      // page). If the user is already signed into Zoom it loads and ZAC stays
      // in control — immediate takeover, no sign-in detour. If not signed in,
      // Zoom bounces to its login page and the release effect hands control
      // back so the user can sign in; login-detect then re-fires the takeover.
      setAgentControl(true);
      setPendingNavigateUrl(ZOOM_S2S_CREATE_URL);
      addAssistantMessage(zoomAckText(purpose), undefined, undefined, { once: true });
      return;
    }
    // Enable-write bridge (D3): the plan's card said the Zoom connection is read-only. Keep the plan so the
    // enablement flow (ChatPage's ?setup=write effect opens the browser panel and runs writeEnableStore.run())
    // opens it once write access is on, instead of sending to the brain.
    if (value === 'enable-write') {
      clearMessageInteractive(message.id);
      const plan = message.interactive?.plan;
      const { currentConversationId } = useChatStore.getState();
      const scopes = [...new Set(plan?.steps.flatMap((step) => step.missingScopes) ?? [])];
      if (plan) useWriteEnableStore.getState().setPendingPlan({ planId: plan.id, ...(currentConversationId ? { conversationId: currentConversationId } : {}), ...(scopes.length > 0 ? { scopes } : {}) });
      navigate('/chat?setup=write');
      return;
    }
    // "Not now" on the enable-write prompt — just dismiss the buttons.
    if (value === 'cancel') {
      clearMessageInteractive(message.id);
      return;
    }
    // Send the selected option as a new user message
    sendMessage(value);
  };

  return (
    <div className={cn('flex flex-col', grouped ? 'pt-0.5 pb-1' : 'py-1.5', isUser ? 'items-end' : 'items-start')}>
      {/* Avatar sits alongside the bubble — once per same-role run; grouped
          follow-ups get a spacer so bubbles stay aligned. */}
      <div className={cn('flex items-end gap-2 max-w-[85%]', isUser ? 'justify-end' : 'justify-start')}>
        {!isUser && (grouped ? (
          <div className="w-9 shrink-0" aria-hidden />
        ) : (
          <video
            src="/zac-avatar.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="h-9 w-9 shrink-0 rounded-lg object-cover"
            aria-label="ZAC"
          />
        ))}
        <div
          className={cn(
            'px-4 py-3 text-foreground',
            isUser
              // User: asymmetric corners, tail on bottom-right
              ? 'rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl rounded-br-sm bg-secondary'
              // Assistant: asymmetric corners, tail on bottom-left, subtle bg
              : 'rounded-tl-2xl rounded-tr-2xl rounded-br-2xl rounded-bl-sm bg-white/[0.04]'
          )}
        >
          <div className="prose dark:prose-invert max-w-none text-foreground">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {normalizeMarkdownTables(message.content)}
            </ReactMarkdown>
          </div>

          {message.artifacts && message.artifacts.length > 0 && (
            <div className="mt-4 space-y-4">
              {message.artifacts.map((artifact) => (
                <ArtifactRenderer key={artifact.id} artifact={artifact} />
              ))}
            </div>
          )}

          {/* Citation markers and feedback row */}
          {message.role === 'assistant' && (
            (message.citations && message.citations.length > 0) ||
            message.confidenceScore !== undefined
          ) && (
            <div className="mt-3 flex items-center gap-4 flex-wrap">
              {message.citations && message.citations.length > 0 && (
                <CitationList citations={message.citations} />
              )}
              {message.confidenceScore !== undefined && (
                <ConfidenceBadge
                  score={message.confidenceScore}
                  rationale={message.confidenceRationale}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Interactive options — below the bubble, indented to clear the avatar gutter */}
      {message.interactive && (
        <div className={cn(!isUser && 'ml-11')}>
          <InteractiveOptions
            interactive={message.interactive}
            onSelect={handleOptionSelect}
            disabled={isStreaming}
            onReplace={replaceCard}
          />
        </div>
      )}
    </div>
  );
}
